import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Container, Paper, Grid, Divider } from '@mui/material';
import ForestRoundedIcon from '@mui/icons-material/ForestRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';

const services = [
  {
    title: 'FSC Veneer Trading',
    description: 'We source high-quality veneer from FSC-certified forests in Gabon.',
    icon: ForestRoundedIcon,
  },
  {
    title: 'FSC Plywood Export',
    description: 'Certified plywood exported with precision and care.',
    icon: LayersRoundedIcon,
  },
  {
    title: 'Zero-Stock Trading Model',
    description: 'Fast, efficient, and lean operations without stock holding.',
    icon: Inventory2RoundedIcon,
  },
  {
    title: 'Custom Global Logistics',
    description: 'Tailored export plans to match volume, country, and time.',
    icon: LocalShippingRoundedIcon,
  },
  {
    title: 'Strategic Market Insights',
    description: 'We offer analytics to help your business scale globally.',
    icon: AnalyticsRoundedIcon,
  },
];

const WhatWeOffer = () => {
  const [visiblePanels, setVisiblePanels] = useState(Array(services.length).fill(false));
  const panelRefs = useRef([]);

  useEffect(() => {
    const observers = panelRefs.current.map((ref, idx) => {
      if (!ref) return null;
      return new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisiblePanels((prev) => {
              const updated = [...prev];
              updated[idx] = true;
              return updated;
            });
            observers[idx]?.disconnect();
          }
        },
        { threshold: 0.2 }
      );
    });
    panelRefs.current.forEach((ref, idx) => {
      if (ref && observers[idx]) observers[idx].observe(ref);
    });
    return () => observers.forEach((obs) => obs && obs.disconnect());
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100%', py: { xs: 0, md: 0 }, overflow: 'hidden', px: { xs: 0.5, sm: 0 } }}>
      {/* Background Image */}
      <Box
        component="img"
        src="/banner/banner5.jpeg"
        alt="What We Offer Background"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 1,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      {/* Gradient Overlay for depth */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.65) 100%)',
        }}
      />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 3, px: { xs: 0.5, sm: 0 } }}>
        <Box sx={{ textAlign: 'center', mb: 0.5, mt: 0.5 }}>
          <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5, letterSpacing: '0.08em', color: '#fff', textTransform: 'uppercase', textShadow: '0 4px 24px rgba(0,0,0,0.7)', fontSize: { xs: '1.4rem', sm: '2rem', md: '2.5rem', lg: '3rem' } }}>
            What We Offer
          </Typography>
          <Box sx={{ width: 60, height: 4, mx: 'auto', borderRadius: 2, background: 'linear-gradient(90deg, #A67C52 0%, #7B5E3B 100%)', mb: 0.5 }} />
        </Box>
      </Container>
      {services.map((service, idx) => {
        const Icon = service.icon;
        return (
          <Box
            key={service.title}
            ref={el => panelRefs.current[idx] = el}
            sx={{
              position: 'relative',
              width: '100%',
              minHeight: { xs: 90, md: 120 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              py: { xs: 0.5, md: 1 },
              px: { xs: 0.5, sm: 0 },
            }}
          >
            {/* Giant ghosted icon */}
            <Icon
              sx={{
                position: 'absolute',
                left: { xs: '50%', md: 0 },
                top: '50%',
                transform: { xs: 'translate(-50%, -50%)', md: 'translateY(-50%)' },
                fontSize: { xs: 40, sm: 70, md: 90 },
                color: '#fff',
                opacity: 0.10,
                zIndex: 1,
                pointerEvents: 'none',
                userSelect: 'none',
                filter: 'blur(0.5px)',
              }}
            />
            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, px: { xs: 0.5, sm: 0 } }}>
              <Grid container justifyContent="center">
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'relative',
                      mx: { xs: 'auto', md: 0 },
                      p: { xs: 1, md: 2 },
                      borderRadius: 4,
                      boxShadow: '0 4px 16px 0 rgba(166,124,82,0.10)',
                      bgcolor: 'rgba(255,255,255,0.93)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'center', md: 'flex-start' },
                      minHeight: 60,
                      maxWidth: 420,
                      width: '100%',
                      opacity: visiblePanels[idx] ? 1 : 0,
                      transform: visiblePanels[idx] ? 'translateY(0)' : 'translateY(20px)',
                      transition: 'opacity 0.6s cubic-bezier(.23,1.01,.32,1), transform 0.6s cubic-bezier(.23,1.01,.32,1)',
                      border: '1.5px solid #e0c9a6',
                      background: 'linear-gradient(135deg, #fffbe9 0%, #f9f6f2 100%)',
                      '&:hover': {
                        boxShadow: '0 8px 32px 0 rgba(166,124,82,0.18)',
                        transform: 'scale(1.025)',
                        borderColor: '#A67C52',
                      },
                    }}
                  >
                    <Box sx={{ position: 'absolute', top: 10, right: 10, opacity: 0.18 }}>
                      <Icon sx={{ fontSize: 24, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5, color: 'primary.main', letterSpacing: '0.04em', fontSize: { xs: '1rem', md: '1.2rem' }, textAlign: { xs: 'center', md: 'left' } }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.92rem', md: '1.01rem' }, textAlign: { xs: 'center', md: 'left' } }}>
                      {service.description}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
            {idx < services.length - 1 && (
              <Divider sx={{ position: 'absolute', left: '10%', right: '10%', bottom: 0, bgcolor: 'rgba(166,124,82,0.18)', height: 2, zIndex: 2 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default WhatWeOffer; 